import { addDays, differenceInDays, isLeapYear } from "date-fns";

import {
  BillingDocumentIntegrationStatus,
  BillingDocumentStatus,
} from "constants/billingDocuments";

import { getDateFromISO8601DateString } from "./timeFormats";

export const INTEREST_NOT_CHARGABLE_DOCUMENT_STATUSES = [
  BillingDocumentStatus.PAID,
  BillingDocumentStatus.DRAFT,
  BillingDocumentStatus.EXPORT_ERROR,
];
export const INTEREST_NOT_CHARGABLE_INTEGRATION_DOCUMENT_STATUSES = [
  BillingDocumentIntegrationStatus.PAID,
  BillingDocumentIntegrationStatus.DRAFT,
  BillingDocumentIntegrationStatus.VOIDED,
  BillingDocumentIntegrationStatus.DELETED,
];

/**
 * Billing documents currently only contain pricing in dollars
 * This will return the interest in cents.
 */
export const calculateInterestChargeCentsAndDaysToCharge = (
  billingDocument,
  interestSettings = {},
  chargeFromDate = null,
  chargeToDate = new Date(),
) => {
  const { daysOverdueThreshold = 0, interestRate } = interestSettings;
  const {
    dueDate,
    status,
    integrationStatus,
    totalAmountDollars,
    latestInterestChargeDate,
    totalPaidCents,
  } = billingDocument;

  const formattedDueDate = addDays(getDateFromISO8601DateString(dueDate), 1);
  const dueFromDatePlusThreshold = addDays(
    formattedDueDate,
    daysOverdueThreshold,
  );

  const shouldInterestBeCharged =
    !INTEREST_NOT_CHARGABLE_DOCUMENT_STATUSES.includes(status) &&
    !INTEREST_NOT_CHARGABLE_INTEGRATION_DOCUMENT_STATUSES.includes(
      integrationStatus,
    ) &&
    new Date() > dueFromDatePlusThreshold; // if there current day is greater than the dueDate plus threshold it is overdue

  if (shouldInterestBeCharged) {
    // Now we know that interest should be charged we now need to determine when interest should be charged from.
    // By default it is just from the due date
    let calculatedChargeFromDate = formattedDueDate;

    // If we have passed a chargeFrom Date in then we check if it is greater than the default calculatedChargeFromDate
    // This is to cover if the interest has become chargable this month and we only want to charge interest from then.
    if (chargeFromDate && chargeFromDate > calculatedChargeFromDate) {
      calculatedChargeFromDate = chargeFromDate;
    }

    // However if there was a interest charge date, we should override as that is now the 'due date'
    if (latestInterestChargeDate) {
      calculatedChargeFromDate = getDateFromISO8601DateString(
        latestInterestChargeDate,
      );
    }

    // Calculate the daily interest rate
    const dailyInterestRate =
      interestRate / (isLeapYear(calculatedChargeFromDate) ? 366 : 365);
    // Caclulate how many days interest by getting the diff between our caluclated charge from and charge to date
    const daysToChargeInterest = differenceInDays(
      chargeToDate,
      calculatedChargeFromDate,
    );

    // Convert the total paid amount to dollars
    const totalPaidDollars = totalPaidCents / 100;

    // Minus the amount paid
    const calculatedTotalAmountDollars = totalAmountDollars - totalPaidDollars;

    // Calculate the total interest
    const interestToCharge =
      dailyInterestRate * calculatedTotalAmountDollars * daysToChargeInterest;

    // Return an object with the cents and days of interest.
    return {
      interestToChargeCents: interestToCharge * 100, // Return in cents
      daysOfInterest: daysToChargeInterest, // Include how many days of interest are being charged
    };
  }

  return { interestToChargeCents: 0, daysOfInterest: 0 };
};
